import { RadioButtonWithLabelGroupWidget, RadioButtonWithLabelOption } from '@tableau/widgets';
import * as React from 'react';
import * as _ from 'underscore';

import { sanitize } from '../../../../src/commonInfra/utils/Sanitize';
import { getAppStore } from '../../../app/AppStore';
import { siteUrlsSelector } from '../../../app/selectors/SiteUrlsSelectors';
import { Messages } from '../../../codegen/Localize';
import { Sites } from '../../../ts/siteManagement/services/resources/Sites';
import { AuthenticationProviderSelectorProperties } from '../model/AuthenticationProviderSelectorProperties';
import {
    DefaultAuthenticationProvider,
    IAuthenticationProvider,
    ISiteAuthenticationSettings,
    SiteAuthenticationSettingsService,
    TabIdWithMFAAuthenticationProvider,
} from '../services/SiteAuthenticationSettingsService';
import {
    authenticationProviderOptionDescriptionStyle,
    authenticationProviderOptionStyle,
    authenticationRadioButtonsStyle,
    configureAuthenticationLinkStyle} from '../styles/AuthenticationProviderSelectorStyle';


export const AUTH_LABEL_TEST_ID = 'authentication-label';

export type AuthenticationProviderSelectorProps = AuthenticationProviderSelectorProperties & {
    onProviderChange: (provider: IAuthenticationProvider) => void;
    onProviderIdsChange: (ids: string[]) => void;
    onAuthSettingChange: (authSetting: string) => void;
}

export type AuthenticationProviderSelectorState = {
    defaultProvider: IAuthenticationProvider;
    tabIdMfaProvider: IAuthenticationProvider;
    enabledAdditionalProviders: IAuthenticationProvider[];
    authenticationUrl: string;
    getProviderDescription: (provider: IAuthenticationProvider, inviteNotificationEnabledForSite: boolean) => string;
    getProviderDisplayName: (provider: IAuthenticationProvider) => string;
    inviteNotificationEnabledForSite?: boolean;
    providerButtons: RadioButtonWithLabelOption<string>[];
    radioButtonIndex: number;
}

export class AuthenticationProviderSelector extends React.Component<AuthenticationProviderSelectorProps, AuthenticationProviderSelectorState> {
    private defaultDisplayName = (provider: IAuthenticationProvider) => SiteAuthenticationSettingsService.getProviderDisplayName(provider);
    private defaultDescription = (provider: IAuthenticationProvider) => '';

    constructor(props: AuthenticationProviderSelectorProps) {
        super(props);
        this.state = {
            defaultProvider: DefaultAuthenticationProvider,
            tabIdMfaProvider: TabIdWithMFAAuthenticationProvider,
            enabledAdditionalProviders: [],
            authenticationUrl: '',
            getProviderDescription: this.props.getProviderDescription || this.defaultDescription,
            getProviderDisplayName: this.props.getProviderDisplayName || this.defaultDisplayName,
            providerButtons: [],
            radioButtonIndex: 0
        };
    }

    public componentDidMount() {
        this.setState({authenticationUrl: siteUrlsSelector(getAppStore().getState()).settingsAuthenticationUrl()});
        SiteAuthenticationSettingsService.getSiteAuthenticationSettings()
            .then((settings: ISiteAuthenticationSettings) => {
                this.setState({defaultProvider: settings.defaultProvider});
                this.setState({tabIdMfaProvider: settings.tabIdMfaProvider});
                this.setState({enabledAdditionalProviders: this.getAdditionalProviders(settings)});
                this.setState({radioButtonIndex: this.state.providerButtons.findIndex(button =>
                    button.value === this.props.authenticationProvider.id)});
                this.setProviderIds(settings);

                if (this.state.enabledAdditionalProviders.length === 0) {
                    this.setProvider(settings, this.state.defaultProvider.id);
                }

                Sites.instance.getCurrentSiteSettingsForSiteAdmin()
                .then((data: any) => {
                    this.setState({inviteNotificationEnabledForSite: data.siteAdminSettings.siteInviteEmailEnabled});
                    this.setState({providerButtons: this.getProviderButtons()});
                });
            });
    }

    private setProvider(settings: ISiteAuthenticationSettings, id: string) {
        const authProvider = SiteAuthenticationSettingsService.findProvider(settings, id);
        const authSetting = SiteAuthenticationSettingsService.getAuthSetting(id, settings);
        this.props.onProviderChange(authProvider);
        this.props.onAuthSettingChange(authSetting);
    }

    private setProviderWithOnlyId(id: string, checkedIndex: number) {
        SiteAuthenticationSettingsService.getSiteAuthenticationSettings()
            .then((settings: ISiteAuthenticationSettings) => {
                this.setProvider(settings, id);
                this.setState({radioButtonIndex: this.state.providerButtons.findIndex(button => button.value === id)});
            });
    }

    private setProviderIds(settings: ISiteAuthenticationSettings) {
        const ids = this.props.authenticationProviderIds || [settings.defaultProvider.id];
        this.props.onProviderIdsChange(ids);
    }

    private getAdditionalProviders(settings: ISiteAuthenticationSettings): IAuthenticationProvider[] {
        const additionalProviders =  _.filter([settings.tabIdMfaProvider].concat(settings.externalProviders), (provider: IAuthenticationProvider) => provider.isEnabled);
        return additionalProviders;
    }

    private getProviderButtons(): RadioButtonWithLabelOption<string>[] {
        const defaultProviderButtonLabel = <>
            <div>{this.state.getProviderDisplayName(this.state.defaultProvider)}</div>
            <div style={authenticationProviderOptionDescriptionStyle} dangerouslySetInnerHTML={{__html:
                sanitize(this.state.getProviderDescription(this.state.defaultProvider, this.state.inviteNotificationEnabledForSite))}}/>
        </>;
        const providerButtons = [
            {
                label: defaultProviderButtonLabel,
                testId: 'default-auth-provider-radio-button',
                value: DefaultAuthenticationProvider.id
            }
        ];

        this.state.enabledAdditionalProviders.forEach((provider) => {
            const additionalProviderButtonLabel = <>
                <div>{this.state.getProviderDisplayName(provider)}</div>
                <div style={authenticationProviderOptionDescriptionStyle} dangerouslySetInnerHTML={{__html:
                    sanitize(this.state.getProviderDescription(provider, this.state.inviteNotificationEnabledForSite))}}/>
            </>;

            //TabId + MFA should be first, if enabled
            if (provider.id === this.state.tabIdMfaProvider.id) {
                providerButtons.unshift(
                    {
                        label: additionalProviderButtonLabel,
                        testId: 'tolmfa-auth-provider-radio-button',
                        value: provider.id
                    }
                );
            }
            else {
                providerButtons.push(
                    {
                        label: additionalProviderButtonLabel,
                        testId: 'external-auth-provider-radio-button',
                        value: provider.id
                    }
                );
            }
        });

        return providerButtons;
    }

    private renderAdditionalProviders(): JSX.Element {
        switch(this.state.enabledAdditionalProviders.length) {
            case 0:
                return (
                <div>
                    <div style={authenticationProviderOptionStyle} data-tb-test-id={AUTH_LABEL_TEST_ID}>
                        {this.state.getProviderDisplayName(this.state.defaultProvider)}
                        &nbsp;
                    </div>
                    <div style={authenticationProviderOptionDescriptionStyle} dangerouslySetInnerHTML={{__html:
                        sanitize(this.state.getProviderDescription(this.state.defaultProvider, this.state.inviteNotificationEnabledForSite))}}/>
                    <div style={configureAuthenticationLinkStyle}>
                        <a href={this.state.authenticationUrl}>
                            {Messages.configureAdditionalAuthenticationMethods()}
                        </a>
                    </div>
                </div>
                );
            default:
                return (
                <div style={authenticationRadioButtonsStyle}>
                    <RadioButtonWithLabelGroupWidget
                    buttonList={this.state.providerButtons}
                    checkedIndex={this.state.radioButtonIndex}
                    handleChange={this.setProviderWithOnlyId.bind(this)}/>
                </div>
                );
        }
    }

    public render(): JSX.Element {
        return (
            <div>
                {this.renderAdditionalProviders()}
            </div>
        );
    }
}